import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function PaymentModal({ isOpen, onClose }) {
    const [formData, setFormData] = useState({
        name: '',
        number: '',
        amount: ''
    });
    const [status, setStatus] = useState('IDLE'); // IDLE, LOADING, POLLING, SUCCESS, FAILED
    const [message, setMessage] = useState('');

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const pollTransactionParams = async (tranId, attempts = 0) => {
        if (attempts >= 40) { // 2 minutes (40 * 3s = 120s)
            setStatus('FAILED');
            setMessage('Transaction timed out.');
            return;
        }

        try {
            const response = await fetch(`https://api.fastlipa.com/api/status-transaction?tranid=${tranId}`, {
                headers: {
                    "Authorization": "Bearer FastLipa_sg72AN0M2Yin2tHAAAn5v"
                }
            });
            const data = await response.json();

            // Check nested data payment_status to be robust
            const transactionStatus = data.data?.payment_status;

            if (transactionStatus === 'COMPLETED') {
                setStatus('SUCCESS');
                setMessage('Payment successful! Thank you!');
                // Close modal after success after a short delay
                setTimeout(onClose, 3000);
            } else if (transactionStatus === 'PENDING') {
                setTimeout(() => pollTransactionParams(tranId, attempts + 1), 3000);
            } else {
                console.log('Unexpected status:', transactionStatus, data);
                // If status is neither COMPLETED nor PENDING, it might be a failure.
                // Safety net: if transactionStatus is undefined but data.status is 'success' (wrapper), we might need to retry if data.data is missing for some reason,
                // but assuming user input implies structure is reliable if data exists.
                if (data.status === 'success' && !data.data) {
                    // Maybe it's still processing?
                    setTimeout(() => pollTransactionParams(tranId, attempts + 1), 3000);
                } else {
                    setStatus('FAILED');
                    setMessage('Transaction failed.');
                }
            }
        } catch (error) {
            console.error(error);
            // Continue polling on network error
            setTimeout(() => pollTransactionParams(tranId, attempts + 1), 3000);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('LOADING');
        setMessage('Initiating payment...');

        try {
            const response = await fetch("https://api.fastlipa.com/api/create-transaction", {
                method: "POST",
                headers: {
                    "Authorization": "Bearer FastLipa_sg72AN0M2Yin2tHAAAn5v",
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    number: formData.number,
                    amount: parseInt(formData.amount),
                    name: formData.name
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setStatus('POLLING');
                setMessage('Please check your phone to confirm the transaction...');
                pollTransactionParams(data.data.tranID);
            } else {
                setStatus('FAILED');
                setMessage(data.message || 'Failed to create transaction.');
            }

        } catch (error) {
            console.error(error);
            setStatus('FAILED');
            setMessage('An error occurred. Please try again.');
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="modal-overlay">
                    <motion.div
                        className="modal-content"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                    >
                        <button className="close-btn" onClick={onClose}>&times;</button>
                        <h2>Send a Gift 🎁</h2>

                        {status === 'SUCCESS' ? (
                            <div className="success-message">
                                <h3>🎉 Payment Successful!</h3>
                                <p>Thank you for your gift!</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label>Your Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        placeholder="John Doe"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Phone Number</label>
                                    <input
                                        type="tel"
                                        name="number"
                                        value={formData.number}
                                        onChange={handleChange}
                                        required
                                        placeholder="0695123456"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Amount</label>
                                    <input
                                        type="number"
                                        name="amount"
                                        value={formData.amount}
                                        onChange={handleChange}
                                        required
                                        min="100"
                                        placeholder="500 minimum"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="btn-pay"
                                    disabled={status === 'LOADING' || status === 'POLLING'}
                                >
                                    {status === 'LOADING' ? 'Processing...' : status === 'POLLING' ? 'Waiting for Confirmation...' : 'Send Gift 💸'}
                                </button>

                                {message && <p className={`status-msg ${status.toLowerCase()}`}>{message}</p>}
                            </form>
                        )}
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
